import { buildPrompt } from '@/ai/utils/promptBuilder'
import { examples } from '@/ai/prompts/common/examples'
import { transformWorkflow } from '@/ai/utils/transformWorkflow'
import { readModelNotes } from '@/ai/prompts/common/instructions'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { getJsonOutputFormatting } from '@/ai/prompts/common/formatting'
import { createSystemMessage, createUserMessage, getRoleCommandAggregateBackground } from '@/ai/utils/promptUtils'

export const buildReadModelFromWorkflowPrompt = ({ eventId, workflow }) => {
  const messages = []

  const systemContent = buildPrompt([systemMessages.EVENT_MODELING_DDD_EXPERT])
  messages.push(createSystemMessage(systemContent))

  const promptSections = []

  const { events, entities, eventIdToLongIdMap } = transformWorkflow(workflow)
  const event = events.find((e) => eventIdToLongIdMap[e.id] === eventId) || {}

  const commandName = event.causedByCommand?.name
  const aggregateName = event.aggregate?.name

  promptSections.push({
    title: 'Current Workflow (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify({ domainEvents: events })
  })

  promptSections.push({
    title: 'Existing Entities (Optional): Use this information if applicable.',
    contentType: 'codeblock',
    language: 'json',
    content: JSON.stringify({ entities })
  })

  promptSections.push({
    title: 'Background Actor, Command, Aggregate',
    content: getRoleCommandAggregateBackground({
      currentStep: event.id,
      roleName: event.role,
      description: event.name,
      commandName: event.causedByCommand?.name,
      aggregateName: event.aggregate?.name
    })
  })

  let dataModelBackgroundSections

  const readModelRequestContent = commandName && aggregateName
    ? `Provide the Read Model as a set of attributes (or data fields) to be displayed to
       the actor BEFORE invoking the command "${commandName}" on the aggregate ${aggregateName}.`
    : `Provide the Read Model as a set of attributes (or data fields) to be displayed to
       the actor BEFORE invoking the command in domain event ${event.id}: ${event.role} - ${event.name}.`

  promptSections.push({
    title: 'Request',
    content: `
      Generate a Read Model (or Query Model) for the actor (human or system) about to make the necessary decisions
      to take action and complete the domain event ${event.id} (${event.role} - ${event.name}). The purpose of the
      Read Model (or Query Model) is to define a query (for one or many items) that displays the current system
      state in a way that is relevant to the actor about to do the task at hand. ${readModelRequestContent}
      Usually, between 2 and 8 attributes or fields are sufficient, but it can be more or less.
      In most of the cases the generated data fields should match with the data fields of the entity
      and include the primary key field (id) of the entity.
      An attribute can be a primitive or a reference to a non-primitive type like an associated entity.
      An attribute or field name must be in camel case format (e.g., 'productName' instead of 'Product Name').
      An attribute or field name must be at most 30 characters long.
    `
  })

  const readModelExample = { readModel: examples.readModelExampleOrder }

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: getJsonOutputFormatting('readModel', JSON.stringify(readModelExample))
  })

  const notes = [readModelNotes[0], readModelNotes[1]]

  if (dataModelBackgroundSections?.length) {
    notes.push(readModelNotes[2], readModelNotes[3])
  }

  promptSections.push({
    title: 'Note',
    contentType: 'bullet',
    content: notes
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return messages
}
